<template>
  <div class="home-echarts-wrap" :style="{ width, height, marginBottom }">
    <div class="home-echarts-header" style="padding: 10px 24px 0 24px;height: auto">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <CosSelect
          v-model:value="levelOrGradeQuery"
          :allowClear="props.allowClear"
          style="width: 145px; margin-left: 10px"
          @change="getData"
        >
          <a-select-option :value="1">档次</a-select-option>
          <a-select-option :value="2">星级</a-select-option>
        </CosSelect>
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts"/>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, watch } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import type { Ref, PropType } from 'vue'
import { getTransactCusDistribution } from '@/api/gucangSales/dataBoard'
import { colors } from '../dataConfig'

interface ItemCommonType {
  name: string
  quantity: number,
  total: number,
  prop: string
}

const props = defineProps({
  title: {
    type: String as PropType<string>,
    default: '成交客户分布情况'
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_lbu'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '350px'
  },
  marginBottom: {
    type: String as PropType<string>,
    default: '8px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  formState: {
    type: Object as PropType<Object>
  }
})

const spinning = ref(false)
const showECharts = ref(false)
const levelNames = ['1档', '2档', '3档', '4档', '5档']
const gradeNames = [1, 2, 3, 4, 5]
// 档次或者评级查询，【1/2,档次/评级
const levelOrGradeQuery = ref(1)
watch(() => props.formState, (val) => {
  getData()
})
const getData = () => {
  const queryForm = {
    ...props.formState,
    levelOrGradeQuery: levelOrGradeQuery.value
  }
  spinning.value = true
  getTransactCusDistribution(queryForm)
    .then((res: any) => {
      // 此处判断切换星级和档次去获取不同的list
      const data = (queryForm.levelOrGradeQuery === 1 ? res.data?.levelList : res.data?.gradeList) as unknown as ItemCommonType[]
      if (!data || data.length === 0) {
        showECharts.value = false
        initChart([])
      } else {
        const transData: ItemCommonType[] = []
        /// 星级去拆分name的长度来看星级
        if (queryForm.levelOrGradeQuery === 2) {
          gradeNames.forEach((grade) => {
            data.forEach((item) => {
              /// 这里过滤下一些脏数据 例如星级和档次为空
              if (item && item.name) {
                if (grade === item.name.length) {
                  transData.push(item)
                }
              }
            })
          })
        } else { /// 档次直接循环
          levelNames.forEach((name) => {
            data.forEach((item) => {
              /// 这里过滤下一些脏数据 例如星级和档次为空
              if (item && item.name) {
                if (name === item.name) {
                  transData.push(item)
                }
              }
            })
          })
        }
        if (transData.length === 0) {
          showECharts.value = false
          initChart([])
        } else {
          showECharts.value = true
          transData.forEach((item) => {
            item.percent = (item?.total ? Number((((item.quantity ?? 0) / item.total) * 100).toFixed(2)) : 0)
            return item
          })
          initChart(transData)
        }
      }
    })
    .finally(() => {
      spinning.value = false
    })
}

const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>, undefined, false)
const initChart = (data: ItemCommonType[]) => {
  setOptions({
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'vertical',
      right: 'left',
      bottom: 'center',
      top: 'middle',
      icon: 'circle',
      // left: 'right',
      // align: 'left', // 文字位置
      padding: [0, 30],
      formatter: (val) => {
        const quality = data.find((item) => item.name === val)?.quantity
        const arr = [
          '{b|' + ((levelOrGradeQuery.value === 2 ? `${val.length ?? 0}级` : val)) + '}',
          '{c|' + quality + '}'
        ]
        return arr.join(' ')
      },
      textStyle: {
        align: 'right',
        rich: {
          b: {
            color: '#6E7B91',
            fontSize: '12px'
          },
          c: {
            fontWeight: 'bold',
            color: '#6E7B91',
            fontSize: '16px'
          }
        }
      }
    },
    series: [
      {
        type: 'pie',
        radius: '76%',
        labelLayout: {
          hideOverlap: false
        },
        bottom: 15,
        right: 150,
        minAngle: 1,
        data: data.map((item, index) => ({
          value: item.quantity,
          name: item.name,
          prop: item.prop,
          label: {
            show: item.percent && item.percent > 10,
            position: 'inside',
            color: '#fff',
            /// 如果需要设置字体 可以添加 textStyle: {color:'', fontsize: ''}
            formatter: (val) => {
              return (levelOrGradeQuery.value === 2 ? `${val.data?.name.length ?? 0}级` : val.data?.name) + '\n' + val.data?.prop
            },
            emphasis: {
              show: true // 鼠标悬浮时显示标签
            }
          },
          itemStyle: {
            color: index < colors.length ? colors[index] : undefined
          }
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  })
}
onBeforeMount(() => getData())
</script>
<script lang="ts">
export default { name: 'TransactCustomerDistribution' }
</script>

<style lang="scss" scoped></style>
